import { useEffect } from 'react';

export interface DocumentMeta {
  title?: string;
  description?: string;
  canonical?: string;
  noindex?: boolean;
  ogImage?: string;
  ogType?: string;
  jsonLd?: object | object[];
}

const DEFAULT_ROBOTS = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

export function useDocumentMeta(meta: DocumentMeta) {
  const key = JSON.stringify(meta);
  useEffect(() => {
    if (meta.title) document.title = meta.title;

    if (meta.description) {
      setMeta('description', meta.description);
      setMeta('og:description', meta.description, 'property');
      setMeta('twitter:description', meta.description);
    }

    if (meta.title) {
      setMeta('og:title', meta.title, 'property');
      setMeta('twitter:title', meta.title);
    }

    if (meta.ogImage) {
      setMeta('og:image', meta.ogImage, 'property');
      setMeta('twitter:image', meta.ogImage);
    }

    setMeta('og:type', meta.ogType || 'website', 'property');

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    const canonicalHref = meta.canonical || window.location.origin + window.location.pathname;
    canonical.href = canonicalHref;
    setMeta('og:url', canonicalHref, 'property');

    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.content = meta.noindex ? 'noindex, nofollow' : DEFAULT_ROBOTS;

    document.querySelectorAll('script[data-dynamic-jsonld]').forEach((s) => s.remove());
    const lds = meta.jsonLd ? (Array.isArray(meta.jsonLd) ? meta.jsonLd : [meta.jsonLd]) : [];
    for (const ld of lds) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-dynamic-jsonld', 'true');
      script.textContent = JSON.stringify(ld);
      document.head.appendChild(script);
    }

    return () => {
      document.querySelectorAll('script[data-dynamic-jsonld]').forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

export function siteUrl(path = '/'): string {
  const base = (typeof window !== 'undefined' ? window.location.origin : 'https://runmorechile.cl');
  return base.replace(/\/$/, '') + (path.startsWith('/') ? path : `/${path}`);
}
